import { Box, Divider, TableCell, TableRow, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import { fileNameFormat, reduceObj } from "../../../common/services/Utilities";
import LoadingIndicator from "../../LoadingIndicator";
import SearchPills from "../../../containers/SearchPills";
import { ApiBaseUrl } from "../../../config";
import { fetchData } from "../../../common/services/ApiClient";
import React from "react";
import {
  additionalHeaderColor,
  primaryHeaderColor,
  secondaryHeaderColor,
  textHeaderColor,
} from "../../../refdata/constants";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import {
  addDataIfNull,
  transformData,
  transformDataForDownload,
} from "./SummaryTableUtilities";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import ShowHideButton from "../../ShowHideButton";

const PREFIX = "SummaryTablesByStateCharter";

const classes = {
  groupHover: `${PREFIX}-groupHover`,
  group: `${PREFIX}-group`,
  tableCellDarken: `${PREFIX}-tableCellDarken`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.groupHover}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      textDecoration: "underline",
      cursor: "pointer",
    },
  },

  [`& .${classes.group}`]: {
    textDecoration: "underline",
  },

  [`& .${classes.tableCellDarken}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
}));

interface Props {
  appState: Query;
}

interface CountsObject {
  count: number;
  unique_count: number;
  sum_DEPSUMBR: number;
}

interface CharterObject {
  CHARTER: string;
  count: number;
  unique_count: number;
  sum_DEPSUMBR: number;
}

export default function SummaryTablesByStateCharter({ appState }: Props) {
  const navigate = useNavigate();
  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        "Summary_Tables_By_State_Charter_Class_formatted",
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      footnotes: [
        appState.filters.lastYear === "1994"
          ? "*Includes 18 conservatorships"
          : null,
      ].filter(Boolean) as string[],
    },
  ];

  const [downloadData, setDownloadData] = React.useState<KeyValue[]>();

  const [loading, setLoading] = React.useState(false);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [resultTableData, setResultTableData] = React.useState<KeyValue>({});
  const [isAfter2011, setIsAfter2011] = React.useState(false);
  const [previousYear, setPreviousYear] = React.useState(
    appState.filters.lastYear,
  );
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "State",
      value: `${appState.filters.stalpbr}`,
      search: false,
      static: true,
      formKey: "stalpbr",
    },
  ];
  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );

  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        Individual State Totals by Charter Class
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={apiData}
        downloadData={downloadData}
      />
    </Box>
  );

  const tableHeaders: TableHeader[][] = [
    [
      {
        title: "",
        styles: {
          backgroundColor: primaryHeaderColor,
          color: textHeaderColor,
          textAlign: "left",
        },
        colSpan: 1,
      },
      {
        title: "All Institutions",
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
      {
        title: "Headquartered In State",
        styles: {
          backgroundColor: additionalHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
      {
        title: "Headquartered Out-of-State",
        styles: {
          backgroundColor: secondaryHeaderColor,
          color: textHeaderColor,
          borderBottom: "1px solid",
        },
        colSpan: 3,
      },
    ],
    (() => {
      const headerValues = [
        "Institutions",
        "Offices",
        "Deposits (Millions USD)",
      ];

      const firstHeader: TableHeader[] = [
        {
          title: "State Charter Class",
          styles: {
            backgroundColor: primaryHeaderColor,
            color: textHeaderColor,
            textAlign: "left",
            minWidth: 280,
          },
          colSpan: 1,
          apiField: "",
        },
      ];

      const allInstitutions: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: secondaryHeaderColor,
              color: textHeaderColor,
              minWidth: header === "Deposits (Millions USD)" ? 65 : 50,
              textAlign: "center",
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );
      const headquarteredInState: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: additionalHeaderColor,
              color: textHeaderColor,
              minWidth: header === "Deposits (Millions USD)" ? 65 : 50,
              textAlign: "center",
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );
      const headquarteredOutOfState: TableHeader[] = headerValues.map(
        (header: string) => {
          return {
            title: header,
            styles: {
              backgroundColor: secondaryHeaderColor,
              color: textHeaderColor,
              minWidth: header === "Deposits (Millions USD)" ? 65 : 50,
              textAlign: "center",
            },
            colSpan: 1,
            apiField: "",
          };
        },
      );

      return [
        ...firstHeader,
        ...allInstitutions,
        ...headquarteredInState,
        ...headquarteredOutOfState,
      ];
    })(),
  ];

  const rowNames: KeyValue = React.useMemo(() => {
    const style1 = {
      fontWeight: "bold",
      textAlign: "left",
      backgroundColor: "rgba(0, 0, 0, 0.03)",
    };
    const style2 = {
      textAlign: "left",
      textIndent: "10px",
      backgroundColor: "rgba(0, 0, 0, 0.03)",
    };
    const style3 = {
      textAlign: "left",
      textIndent: "20px",
      fontStyle: "italic",
      backgroundColor: "rgba(0, 0, 0, 0.03)",
    };

    return {
      "Commercial Banks": style1,
      "National Charter": style2,
      "State Charter": style2,
      "Federal Reserve Member": style3,
      "Federal Reserve Nonmember": style3,
      "Savings Institutions": style1,
      "Federal Charter Savings Associations": style2,
      "State Charter Savings Institutions": style2,
      "FDIC-Supervised Savings Banks": style3,
      "OTS-Supervised Savings Associations": style3,
      "Federal Reserve-Supervised": style3,
      "U.S. Branches of Foreign Banks": style1,
      "Totals": {
        textAlign: "left",
        fontWeight: "bold",
        backgroundColor: primaryHeaderColor,
        color: textHeaderColor,
      },
    };
  }, []);
  const headerRows = [
    "Commercial Banks",
    "Savings Institutions",
    "U.S. Branches of Foreign Banks",
  ];
  const addFedDataIfNull = (data: CharterObject[]): CharterObject[] => {
    const dataArray = [];
    for (const dataElem of data) {
      dataArray.push(dataElem.CHARTER);
    }
    if (!dataArray.includes("FED")) {
      data.push({ CHARTER: "FED", count: 0, unique_count: 0, sum_DEPSUMBR: 0 });
    }
    if (!dataArray.includes("STATE")) {
      data.push({
        CHARTER: "STATE",
        count: 0,
        unique_count: 0,
        sum_DEPSUMBR: 0,
      });
    }
    return data;
  };

  function transformFedData(
    inputData: {
      CHARTER: string;
      count: number;
      unique_count: number;
      sum_DEPSUMBR: number;
    }[],
  ): {
    [key: string]: {
      count: number;
      unique_count: number;
      sum_DEPSUMBR: number;
    };
  } {
    const transformFedData: {
      [key: string]: {
        count: number;
        unique_count: number;
        sum_DEPSUMBR: number;
      };
    } = {};

    for (const item of inputData) {
      transformFedData[item.CHARTER] = {
        count: item.count,
        unique_count: item.unique_count,
        sum_DEPSUMBR: item.sum_DEPSUMBR,
      };
    }

    return transformFedData;
  }

  function subtractCountsObjects(
    obj1: CountsObject,
    obj2: CountsObject,
  ): CountsObject {
    const result: CountsObject = {
      count: obj1.count - (obj2.count ? obj2.count : 0),
      unique_count:
        obj1.unique_count - (obj2.unique_count ? obj2.unique_count : 0),
      sum_DEPSUMBR:
        obj1.sum_DEPSUMBR - (obj2.sum_DEPSUMBR ? obj2.sum_DEPSUMBR : 0),
    };

    return result;
  }

  React.useEffect(() => {
    if (appState.ui.reportType) {
      setLoading(true);
      setPreviousYear(appState.filters.lastYear);
      initialLoad && setInitialLoad(false);
      let isMounted = true;
      if (
        Number(appState.filters.lastYear) >= 1994 &&
        Number(appState.filters.lastYear) <= 2011
      ) {
        setIsAfter2011(false);
      } else {
        setIsAfter2011(true);
      }
      let tableData = reduceObj(
        Object.keys(rowNames).map((rowName: string) => {
          return {
            key: rowName,
            value: {
              all: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
              hqInState: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
              hqOutOfState: { unique_count: 0, count: 0, sum_DEPSUMBR: 0 },
            },
          };
        }),
      );
      (async () => {
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${
              appState.filters.stalpbr
            }"&sort_by=INSURED&sort_order=asc&get_unique=CERT`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${appState.filters.stalpbr}" AND STALP:"${
              appState.filters.stalpbr
            }"&sort_by=INSURED&sort_order=asc&get_unique=CERT`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${
              appState.filters.stalpbr
            }" AND INSURED:CB&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${appState.filters.stalpbr}" AND STALP:"${
              appState.filters.stalpbr
            }" AND INSURED:CB&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${
              appState.filters.stalpbr
            }" AND INSURED:CB AND BKCLASS:SM&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${appState.filters.stalpbr}" AND STALP:"${
              appState.filters.stalpbr
            }" AND BKCLASS:SM AND INSURED:CB&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${
              appState.filters.stalpbr
            }" AND INSURED:CB AND BKCLASS:NM&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND STALPBR:"${appState.filters.stalpbr}" AND STALP:"${
              appState.filters.stalpbr
            }" AND BKCLASS:NM AND INSURED:CB&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND STALPBR:"${
              appState.filters.stalpbr
            }"&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND STALPBR:"${
              appState.filters.stalpbr
            }" AND STALP:"${
              appState.filters.stalpbr
            }"&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND CHARTER:STATE AND REGAGNT:FDIC AND STALPBR:"${
              appState.filters.stalpbr
            }"&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND CHARTER:STATE AND REGAGNT:FDIC AND STALPBR:"${
              appState.filters.stalpbr
            }" AND STALP:"${
              appState.filters.stalpbr
            }"&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND CHARTER:STATE AND REGAGNT:FED,OTS,OCC AND STALPBR:"${
              appState.filters.stalpbr
            }"&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
          fetchData(
            `${ApiBaseUrl()}/sod?agg_by=CHARTER&agg_sum_fields=DEPSUMBR&filters=YEAR:${
              appState.filters.lastYear
            } AND INSURED:SA AND CHARTER:STATE AND REGAGNT:FED,OTS,OCC AND STALPBR:"${
              appState.filters.stalpbr
            }" AND STALP:"${
              appState.filters.stalpbr
            }"&get_unique=CERT&sort_by=CHARTER&sort_order=asc`,
          ),
        ])
          .then((response) => {
            if (isMounted && response[0].data) {
              const all: KeyValue = transformData(
                addDataIfNull(response[0].data),
              );
              tableData["Commercial Banks"]["all"] = all.CB;
              tableData["Savings Institutions"]["all"] = all.SA;
              tableData["U.S. Branches of Foreign Banks"]["all"] = all.IB;

              const hqInState: KeyValue = transformData(
                addDataIfNull(response[1].data),
              );
              tableData["Commercial Banks"]["hqInState"] = hqInState.CB;
              tableData["Savings Institutions"]["hqInState"] = hqInState.SA;
              tableData["U.S. Branches of Foreign Banks"]["hqInState"] =
                hqInState.IB;

              tableData["Commercial Banks"]["hqOutOfState"] =
                subtractCountsObjects(all.CB, hqInState.CB);
              tableData["Savings Institutions"]["hqOutOfState"] =
                subtractCountsObjects(all.SA, hqInState.SA);
              tableData["U.S. Branches of Foreign Banks"]["hqOutOfState"] =
                subtractCountsObjects(all.IB, hqInState.IB);

              const cbCharterAll: KeyValue = transformFedData(
                addFedDataIfNull(response[2].data),
              );
              tableData["National Charter"]["all"] = cbCharterAll.FED;
              tableData["State Charter"]["all"] = cbCharterAll.STATE;

              const cbCharterInState: KeyValue = transformFedData(
                addFedDataIfNull(response[3].data),
              );
              tableData["National Charter"]["hqInState"] = cbCharterInState.FED;
              tableData["State Charter"]["hqInState"] = cbCharterInState.STATE;

              tableData["National Charter"]["hqOutOfState"] =
                subtractCountsObjects(cbCharterAll.FED, cbCharterInState.FED);
              tableData["State Charter"]["hqOutOfState"] =
                subtractCountsObjects(
                  cbCharterAll.STATE,
                  cbCharterInState.STATE,
                );

              const stateCharterMember: KeyValue = transformFedData(
                addFedDataIfNull(response[4].data),
              );
              tableData["Federal Reserve Member"]["all"] =
                stateCharterMember.STATE;

              const stateCharterMemberHqInState: KeyValue = transformFedData(
                addFedDataIfNull(response[5].data),
              );
              tableData["Federal Reserve Member"]["hqInState"] =
                stateCharterMemberHqInState.STATE;
              tableData["Federal Reserve Member"]["hqOutOfState"] =
                subtractCountsObjects(
                  stateCharterMember.STATE,
                  stateCharterMemberHqInState.STATE,
                );

              const stateCharterNonMember: KeyValue = transformFedData(
                addFedDataIfNull(response[6].data),
              );
              tableData["Federal Reserve Nonmember"]["all"] =
                stateCharterNonMember.STATE;
              const stateCharterNonMemberHqInState: KeyValue = transformFedData(
                addFedDataIfNull(response[7].data),
              );
              tableData["Federal Reserve Nonmember"]["hqInState"] =
                stateCharterNonMemberHqInState.STATE;
              tableData["Federal Reserve Nonmember"]["hqOutOfState"] =
                subtractCountsObjects(
                  stateCharterNonMember.STATE,
                  stateCharterNonMemberHqInState.STATE,
                );

              const fedAndStateSavingsAll: KeyValue = transformFedData(
                addFedDataIfNull(response[8].data),
              );
              tableData["Federal Charter Savings Associations"]["all"] =
                fedAndStateSavingsAll.FED;
              tableData["State Charter Savings Institutions"]["all"] =
                fedAndStateSavingsAll.STATE;
              const fedAndStateSavingsHqInState: KeyValue = transformFedData(
                addFedDataIfNull(response[9].data),
              );
              tableData["Federal Charter Savings Associations"]["hqInState"] =
                fedAndStateSavingsHqInState.FED;
              tableData["State Charter Savings Institutions"]["hqInState"] =
                fedAndStateSavingsHqInState.STATE;
              tableData["Federal Charter Savings Associations"][
                "hqOutOfState"
              ] = subtractCountsObjects(
                fedAndStateSavingsAll.FED,
                fedAndStateSavingsHqInState.FED,
              );
              tableData["State Charter Savings Institutions"]["hqOutOfState"] =
                subtractCountsObjects(
                  fedAndStateSavingsAll.STATE,
                  fedAndStateSavingsHqInState.STATE,
                );

              const fdicSavingsAll: KeyValue = transformFedData(
                addFedDataIfNull(response[10].data),
              );
              tableData["FDIC-Supervised Savings Banks"]["all"] =
                fdicSavingsAll.STATE;

              const fdicSavingsHqInState: KeyValue = transformFedData(
                addFedDataIfNull(response[11].data),
              );
              tableData["FDIC-Supervised Savings Banks"]["hqInState"] =
                fdicSavingsHqInState.STATE;
              tableData["FDIC-Supervised Savings Banks"]["hqOutOfState"] =
                subtractCountsObjects(
                  fdicSavingsAll.STATE,
                  fdicSavingsHqInState.STATE,
                );

              const otsSavingsAll: KeyValue = transformFedData(
                addFedDataIfNull(response[12].data),
              );
              tableData["OTS-Supervised Savings Associations"]["all"] =
                otsSavingsAll.STATE;
              const otsSavingsHqInState: KeyValue = transformFedData(
                addFedDataIfNull(response[13].data),
              );
              tableData["OTS-Supervised Savings Associations"]["hqInState"] =
                otsSavingsHqInState.STATE;
              tableData["OTS-Supervised Savings Associations"]["hqOutOfState"] =
                subtractCountsObjects(
                  otsSavingsAll.STATE,
                  otsSavingsHqInState.STATE,
                );

              const occSavingsAll: KeyValue = transformFedData(
                addFedDataIfNull(response[12].data),
              );
              tableData["Federal Reserve-Supervised"]["all"] =
                occSavingsAll.STATE;
              const occSavingsHqInState: KeyValue = transformFedData(
                addFedDataIfNull(response[13].data),
              );
              tableData["Federal Reserve-Supervised"]["hqInState"] =
                occSavingsHqInState.STATE;
              tableData["Federal Reserve-Supervised"]["hqOutOfState"] =
                subtractCountsObjects(
                  occSavingsAll.STATE,
                  occSavingsHqInState.STATE,
                );

              const totalAll: CountsObject = response[0].totals;

              totalAll["count"] =
                all.CB["count"] + all.SA["count"] + all.IB["count"];
              totalAll["unique_count"] =
                all.CB["unique_count"] +
                all.SA["unique_count"] +
                all.IB["unique_count"];
              tableData["Totals"]["all"] = totalAll;

              const totalHqInState: CountsObject = response[1].totals;
              totalHqInState["count"] =
                hqInState.CB["count"] +
                hqInState.SA["count"] +
                hqInState.IB["count"];
              totalHqInState["unique_count"] =
                hqInState.CB["unique_count"] +
                hqInState.SA["unique_count"] +
                hqInState.IB["unique_count"];
              tableData["Totals"]["hqInState"] = totalHqInState;
              tableData["Totals"]["hqOutOfState"] = subtractCountsObjects(
                totalAll,
                totalHqInState,
              );
              const resultForDownload = transformDataForDownload(
                tableData,
                `6/30/${appState.filters.lastYear}`,
                "stateCharterClass",
                `${appState.filters.stalpbr}`,
              );
              setDownloadData(resultForDownload);
              setResultTableData(tableData);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [
    appState.filters,
    initialLoad,
    previousYear,
    appState.ui,
    appState.filters.stalpbr,
    rowNames,
  ]);

  const determineRowStyle = (key: string) => {
    const rowStyle: React.CSSProperties = rowNames[key] ? rowNames[key] : {};
    return rowStyle;
  };

  const [groupHover, setGroupHover] = React.useState("");

  const tableCellProps = (
    instType: string,
    key: string,
    int: number,
    value: number,
  ) => {
    let props: KeyValue = { className: int % 2 ? classes.tableCellDarken : "" };
    if (value > 0 && key !== "Totals") {
      props = {
        onMouseEnter: () => setGroupHover(`${instType}_${int}`),
        onMouseLeave: () => setGroupHover(""),
        onFocus: () => setGroupHover(`${instType}_${int}`),
        onBlur: () => setGroupHover(""),
        onClick: () =>
          navigate({
            search: `?summaryTableLevelTwo=true&instType=${instType}&summaryTableType=${key}&stalpbr=${appState.filters.stalpbr}`,
          }),
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            navigate({
              search: `?summaryTableLevelTwo=true&instType=${instType}&summaryTableType=${key}&stalpbr=${appState.filters.stalpbr}`,
            });
          }
        },
        tabIndex: 0,
        className: clsx(
          int % 2 ? classes.tableCellDarken : "",
          groupHover === `${instType}_${int}`
            ? classes.groupHover
            : classes.group,
        ),
      };
    }
    return props;
  };

  const rowsToShow = { ...rowNames };
  if (isAfter2011) {
    delete rowsToShow["OTS-Supervised Savings Associations"];
  } else {
    delete rowsToShow["Federal Reserve-Supervised"];
  }

  const rows = Object.keys(rowsToShow)
    .map((key, int) => {
      const rowStyle = determineRowStyle(key);
      let data =
        Object.keys(resultTableData).length > 0 ? resultTableData[key] : {};
      const tableCells = (cellKey: string) => {
        let cellStyle: React.CSSProperties = {
          fontWeight: "bold",
          textAlign: "center",
        };
        let cellStyleDeposits: React.CSSProperties = {
          fontWeight: "bold",
          textAlign: "right",
        };
        if (!headerRows.includes(key)) {
          cellStyle.fontWeight = "normal";
        }

        if (groupHover !== `${cellKey}_${int}`) {
          if (cellKey !== "hqInState") {
            cellStyle.backgroundColor = "#fff";
          }
          if (key === "Totals" && cellKey === "hqInState") {
            cellStyle.backgroundColor = additionalHeaderColor;
            cellStyle.color = textHeaderColor;
            cellStyleDeposits.backgroundColor = additionalHeaderColor;
            cellStyleDeposits.color = textHeaderColor;
          } else if (key === "Totals") {
            cellStyle.backgroundColor = secondaryHeaderColor;
            cellStyle.color = textHeaderColor;
            cellStyleDeposits.backgroundColor = secondaryHeaderColor;
            cellStyleDeposits.color = textHeaderColor;
          } else if (cellKey === "hqInState") {
            cellStyle.backgroundColor = "rgba(0, 0, 0, 0.03)";
            cellStyleDeposits.backgroundColor = "rgba(0, 0, 0, 0.03)";
          }
        }
        return (
          <>
            <TableCell
              {...tableCellProps(cellKey, key, int, data[cellKey].unique_count)}
              style={{
                ...cellStyle,
                ...(key !== "Totals"
                  ? { color: data[cellKey].unique_count > 0 ? "blue" : "grey" }
                  : {}),
              }}
            >
              {Object.keys(data).length > 0 &&
              data[cellKey] &&
              data[cellKey].unique_count
                ? data[cellKey].unique_count.toLocaleString()
                : 0}
            </TableCell>
            <TableCell
              {...tableCellProps(cellKey, key, int, data[cellKey].count)}
              style={{
                ...cellStyle,
                ...(key !== "Totals"
                  ? { color: data[cellKey].count > 0 ? "blue" : "grey" }
                  : {}),
              }}
            >
              {Object.keys(data).length > 0 &&
              data[cellKey] &&
              data[cellKey].count
                ? data[cellKey].count.toLocaleString()
                : 0}
            </TableCell>
            <TableCell
              {...tableCellProps(cellKey, key, int, data[cellKey].sum_DEPSUMBR)}
              style={{
                ...cellStyle,
                ...(key !== "Totals"
                  ? { color: data[cellKey].sum_DEPSUMBR > 0 ? "blue" : "grey" }
                  : {}),
              }}
            >
              {Object.keys(data).length > 0 &&
              data[cellKey] &&
              data[cellKey].sum_DEPSUMBR
                ? (data[cellKey].sum_DEPSUMBR / 1000).toLocaleString(
                    undefined,
                    {
                      maximumFractionDigits: 0,
                    },
                  )
                : 0}
            </TableCell>
          </>
        );
      };

      return (
        <TableRow
          tabIndex={0}
          id={`summaryByState_row_${int}`}
          key={`TD_${key}`}
        >
          <TableCell style={rowStyle}>
            {key === "Savings Institutions" &&
            appState.filters.lastYear === "1994"
              ? key.concat("*")
              : key}
          </TableCell>
          {Object.keys(data).length > 0 && tableCells("all")}
          {Object.keys(data).length > 0 && tableCells("hqInState")}
          {Object.keys(data).length > 0 && tableCells("hqOutOfState")}
        </TableRow>
      );
    })
    .filter(Boolean);

  const tableBody = loading ? (
    <TableRow style={{ position: "relative", height: 125 }}>
      <TableCell
        style={{
          position: "absolute",
          width: "100%",
          backgroundColor: "#fff",
        }}
      >
        <LoadingIndicator
          text="Loading"
          spinnerSize={32}
          textSx={{
            fontSize: 20,
            width: "100%",
            textAlign: "center",
          }}
          spinnerSx={{
            marginTop: 24,
            width: "100%",
            textAlign: "center",
          }}
        />
      </TableCell>
    </TableRow>
  ) : (
    <>{rows}</>
  );

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <Root>
        <MultiHeaderTable
          useStickyHeader
          tableBody={tableBody}
          tableHeaders={tableHeaders}
        />
      </Root>
      {appState.filters.lastYear === "1994" && (
        <Typography
          sx={{
            color: (theme) => theme.palette.primary.main,
            width: 700,
            minWidth: 700,
            fontSize: 16,
            whiteSpace: "nowrap",
          }}
        >
          * Includes 18 conservatorships
        </Typography>
      )}
    </>
  );
}
